import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { type ReactNode, useState } from 'react'
import toast from 'react-hot-toast'

/**
 * Props for QueryProvider component
 */
export interface QueryProviderProps {
  children: ReactNode
  /**
   * Whether to show React Query DevTools.
   * Defaults to true in development, false in production.
   */
  showDevTools?: boolean
  /**
   * Custom QueryClient instance.
   * If not provided, a default client with sensible defaults will be created.
   */
  queryClient?: QueryClient
}

/**
 * Global error handler for React Query query errors.
 * Shows toast notifications for query errors automatically.
 *
 * Components can opt-out of global error handling by setting meta.skipGlobalError = true
 * in the query options.
 *
 * Note: Mutations are not handled here as they typically have their own
 * error handling with specific user-facing messages.
 *
 * @param error - The error that occurred
 * @param query - The query that failed
 */
function handleQueryError(error: unknown, query: { meta?: { skipGlobalError?: boolean } }) {
  // Skip global error handling if explicitly requested via meta
  if (query.meta?.skipGlobalError) {
    return
  }

  // Extract error message
  let errorMessage = 'An error occurred'
  if (error instanceof Error) {
    errorMessage = error.message
  } else if (typeof error === 'string') {
    errorMessage = error
  } else if (error && typeof error === 'object' && 'message' in error) {
    errorMessage = String(error.message)
  }

  // Show toast notification
  toast.error(errorMessage)
}

/**
 * Default query client configuration with sensible defaults for most applications.
 * Includes global error handling for queries that automatically shows toast notifications.
 *
 * @example Opt-out of global error handling for a specific query
 * ```ts
 * useQuery({
 *   queryKey: ['my-query'],
 *   queryFn: fetchData,
 *   meta: { skipGlobalError: true }, // Skip global toast, handle error yourself
 * })
 * ```
 */
function createDefaultQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        handleQueryError(error, query)
      },
    }),
    defaultOptions: {
      queries: {
        // Consider data fresh for 5 minutes
        staleTime: 5 * 60 * 1000,
        // Keep unused data in cache for 10 minutes
        gcTime: 10 * 60 * 1000,
        // Retry failed requests once
        retry: 1,
        // Don't refetch on window focus to reduce unnecessary API calls
        refetchOnWindowFocus: false,
        // Refetch on mount if data is stale
        refetchOnMount: true,
        // Refetch on reconnect
        refetchOnReconnect: true,
      },
      mutations: {
        // Don't retry mutations by default (user actions should be explicit)
        retry: false,
        // Mutations handle their own errors with specific user-facing messages
        // No global error handler needed here
      },
    },
  })
}

/**
 * QueryProvider component that wraps the application with TanStack Query context.
 * Includes React Query DevTools in development mode for debugging.
 *
 * @example
 * ```tsx
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 * ```
 *
 * @example With custom QueryClient
 * ```tsx
 * const customClient = new QueryClient({
 *   defaultOptions: {
 *     queries: {
 *       staleTime: 10 * 60 * 1000, // 10 minutes
 *     },
 *   },
 * })
 *
 * <QueryProvider queryClient={customClient}>
 *   <App />
 * </QueryProvider>
 * ```
 */
export function QueryProvider({ children, showDevTools, queryClient: customQueryClient }: QueryProviderProps) {
  // Use custom client or create default one (only created once per mount)
  const [queryClient] = useState(() => customQueryClient || createDefaultQueryClient())

  // Show devtools in development by default
  const shouldShowDevTools = showDevTools ?? process.env.NODE_ENV === 'development'

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {shouldShowDevTools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}
